import { Link } from "react-router-dom";
import { Palette, Clock, ArrowLeft, ArrowRight, Check, Printer, Download, Zap, Star } from "lucide-react";
import { Button } from "../../components/ui/button";

const ArticlePrintableColoringPagesGenerator = () => {
  return (
    <div className="min-h-screen bg-hero-gradient">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
                <Palette className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-xl text-slate-800" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                ColorWonder
              </span>
            </Link>

            <div className="flex items-center gap-4">
              <Link to="/blog" className="text-slate-600 hover:text-slate-900 font-medium">
                Blog
              </Link>
              <Button
                onClick={() => window.location.href = "/signup"}
                className="h-10 px-6 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold"
              >
                Try Free
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Article Content */}
      <article className="pt-24 pb-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-3xl mx-auto">
          {/* Breadcrumb */}
          <Link to="/blog" className="inline-flex items-center gap-2 text-violet-600 hover:text-violet-700 mb-8">
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Link>

          {/* Header */}
          <header className="mb-12">
            <div className="flex items-center gap-3 text-sm text-slate-500 mb-4">
              <span className="px-3 py-1 bg-pink-100 text-pink-700 rounded-full font-medium">Tools</span>
              <span>•</span>
              <span>Jan 2026</span>
              <span>•</span>
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                6 min read
              </span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-slate-800 mb-6" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Printable Coloring Pages Generator [Free Tool]
            </h1>
            
            <p className="text-xl text-slate-600 leading-relaxed">
              Stop searching for coloring pages online. Generate your own custom, print-ready pages in seconds with ColorWonder's free AI generator. Here's everything you need to know.
            </p>
          </header>

          {/* Quick CTA */}
          <div className="bg-gradient-to-r from-violet-600 to-indigo-600 rounded-2xl p-6 mb-12 text-white">
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-5 h-5" />
              <span className="font-semibold">Skip to the Tool</span>
            </div>
            <p className="text-violet-100 mb-4">
              Ready to create? Jump straight to ColorWonder and generate your first printable coloring page in under 60 seconds.
            </p>
            <Button
              onClick={() => window.location.href = "/signup"}
              className="h-10 px-6 rounded-full bg-white text-violet-600 font-semibold hover:bg-violet-50"
            >
              Try Free Generator →
            </Button>
          </div>

          {/* Table of Contents */}
          <div className="bg-slate-50 rounded-2xl p-6 mb-12">
            <h2 className="font-semibold text-slate-800 mb-4">📋 In This Article</h2>
            <ul className="space-y-2 text-slate-600">
              <li><a href="#why-generator" className="hover:text-violet-600">Why Use a Coloring Page Generator?</a></li>
              <li><a href="#how-it-works" className="hover:text-violet-600">How the Generator Works</a></li>
              <li><a href="#features" className="hover:text-violet-600">Key Features</a></li>
              <li><a href="#print-tips" className="hover:text-violet-600">Tips for Best Print Quality</a></li>
              <li><a href="#faq" className="hover:text-violet-600">FAQs</a></li>
            </ul>
          </div>

          {/* Why Use Generator */}
          <section id="why-generator" className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Why Use a Coloring Page Generator?
            </h2>
            <p className="text-slate-600 mb-6">
              Traditional coloring page websites have the same problem: limited selection and generic designs. You've probably experienced this:
            </p>
            
            <div className="space-y-3 mb-6">
              {[
                "Spending 30+ minutes searching for the 'perfect' coloring page",
                "Finding pages that are too simple or too complex for your child",
                "Seeing the same designs your child has already colored before",
                "Wanting a specific theme (like 'dinosaur astronaut') that doesn't exist"
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-red-50 rounded-xl">
                  <span className="text-red-500">✗</span>
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </div>

            <p className="text-slate-600 mb-4">
              A printable coloring page generator solves all these problems. Instead of searching, you simply <strong>describe what you want</strong> and get a unique, custom page in seconds.
            </p>

            <div className="space-y-3">
              {[
                "Create exactly what your child wants",
                "Get age-appropriate complexity every time",
                "Never run out of new designs",
                "Generate any theme imaginable"
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-green-50 rounded-xl">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </div>
          </section>

          {/* How It Works */}
          <section id="how-it-works" className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              How the Generator Works
            </h2>
            <p className="text-slate-600 mb-6">
              ColorWonder uses advanced AI to transform your text descriptions into beautiful line art. The process is simple:
            </p>

            <div className="grid gap-4">
              {[
                { step: "1", title: "Describe Your Page", desc: "Type what you want to see, like 'cute bunny in a flower garden'" },
                { step: "2", title: "Choose Settings", desc: "Select age group, style, and number of pages" },
                { step: "3", title: "Generate", desc: "AI creates your custom coloring page in ~30 seconds" },
                { step: "4", title: "Download & Print", desc: "Get your page as a high-quality PDF, ready for printing" }
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4 p-4 bg-white rounded-xl border border-slate-100">
                  <span className="w-10 h-10 bg-violet-100 text-violet-600 rounded-full flex items-center justify-center font-bold flex-shrink-0">
                    {item.step}
                  </span>
                  <div>
                    <h4 className="font-semibold text-slate-800">{item.title}</h4>
                    <p className="text-slate-600 text-sm">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Features */}
          <section id="features" className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Key Features
            </h2>
            
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { icon: Printer, title: "Print-Ready Output", desc: "A4 optimized PDFs that print perfectly on standard paper" },
                { icon: Zap, title: "Instant Generation", desc: "Get your coloring page in under 60 seconds" },
                { icon: Star, title: "Clean Line Art", desc: "No unwanted shading – perfect outlines for coloring" },
                { icon: Download, title: "Easy Download", desc: "One-click PDF download, saved to your library" }
              ].map((feature, i) => (
                <div key={i} className="bg-white rounded-xl border border-slate-100 p-5">
                  <feature.icon className="w-8 h-8 text-violet-600 mb-3" />
                  <h4 className="font-semibold text-slate-800 mb-1">{feature.title}</h4>
                  <p className="text-slate-600 text-sm">{feature.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Free vs Paid */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Free vs. Paid Plans
            </h2>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-slate-50 rounded-xl p-5">
                <h4 className="font-semibold text-slate-800 mb-3">Free Plan</h4>
                <ul className="space-y-2 text-slate-600 text-sm">
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> 10 pages per month</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> 3 pages per day</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Basic quality</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> PDF downloads</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Personal use</li>
                </ul>
              </div>
              <div className="bg-violet-50 rounded-xl p-5 border-2 border-violet-200">
                <h4 className="font-semibold text-slate-800 mb-3">Paid Plans</h4>
                <ul className="space-y-2 text-slate-600 text-sm">
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-violet-600" /> 50-300 pages per month</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-violet-600" /> No daily limits</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-violet-600" /> High resolution output</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-violet-600" /> Batch generation</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-violet-600" /> Commercial license</li>
                </ul>
                <p className="mt-3 text-violet-700 text-sm font-medium">Starting at $4.99/month</p>
              </div>
            </div>
          </section>

          {/* Print Tips */}
          <section id="print-tips" className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Tips for Best Print Quality
            </h2>
            
            <div className="bg-violet-50 rounded-2xl p-6">
              <ul className="space-y-3">
                {[
                  { tip: "Use white paper", detail: "Bright white paper makes colors pop more than off-white or cream" },
                  { tip: "Print at 100% scale", detail: "Don't let your printer auto-scale – keep it at 100% for proper sizing" },
                  { tip: "Try card stock for durability", detail: "Thicker paper handles crayons and markers better" },
                  { tip: "Use 'Best Quality' printer setting", detail: "For sharpest lines, use your printer's highest quality option" },
                  { tip: "Let ink dry before coloring", detail: "Wait 30 seconds after printing to prevent smearing" }
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-violet-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                      {i + 1}
                    </span>
                    <div>
                      <span className="font-medium text-slate-800">{item.tip}</span>
                      <span className="text-slate-600"> – {item.detail}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* CTA */}
          <section className="mb-12">
            <div className="bg-gradient-to-r from-violet-600 to-indigo-600 rounded-2xl p-8 text-white text-center">
              <Printer className="w-12 h-12 mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                Create Your Printable Coloring Pages Now
              </h3>
              <p className="text-violet-100 mb-6 max-w-md mx-auto">
                Free to start, no credit card required. Generate your first page in under a minute!
              </p>
              <Button
                onClick={() => window.location.href = "/signup"}
                className="h-14 px-8 rounded-full bg-white text-violet-600 font-semibold text-lg hover:bg-violet-50"
              >
                Try Free Generator
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </section>

          {/* FAQ */}
          <section id="faq" className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-6" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              {[
                {
                  q: "Is ColorWonder really free?",
                  a: "Yes! The free plan gives you 10 pages per month at no cost. No credit card required to sign up."
                },
                {
                  q: "What file format do I get?",
                  a: "All coloring pages are delivered as high-quality PDF files, optimized for A4 paper printing."
                },
                {
                  q: "Can I use these pages for my classroom?",
                  a: "Yes! Teachers can use the free plan for classroom materials. For bulk printing or commercial use, consider a Plus or Pro plan."
                },
                {
                  q: "What if I want more than 10 pages per month?",
                  a: "Our Basic plan ($4.99/mo) gives you 50 pages. Plus ($9.99/mo) provides 150 pages, and Pro ($14.99/mo) offers 300 pages."
                },
                {
                  q: "How do I download my coloring pages?",
                  a: "After generating, click the 'Download PDF' button. Pages are saved to your library for re-downloading anytime."
                }
              ].map((faq, i) => (
                <div key={i} className="bg-white rounded-xl border border-slate-100 p-5">
                  <h4 className="font-semibold text-slate-800 mb-2">{faq.q}</h4>
                  <p className="text-slate-600">{faq.a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Conclusion */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Start Generating Today
            </h2>
            <p className="text-slate-600 mb-4">
              A printable coloring page generator gives you unlimited creative possibilities. No more settling for "close enough" designs – create exactly what you (or your kids) want.
            </p>
            <p className="text-slate-600">
              <Link to="/signup" className="text-violet-600 font-semibold hover:underline">Try ColorWonder free</Link> and see how easy it is to generate custom coloring pages. Your first printable page is just seconds away!
            </p>
          </section>

          {/* Related Articles */}
          <section className="border-t border-slate-200 pt-12">
            <h3 className="text-xl font-bold text-slate-800 mb-6" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Related Articles
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <Link to="/blog/create-coloring-pages-with-ai" className="p-4 bg-white rounded-xl border border-slate-100 hover:border-violet-200 transition-colors">
                <h4 className="font-semibold text-slate-800 mb-1">How to Create Coloring Pages with AI</h4>
                <p className="text-slate-500 text-sm">Complete tutorial for beginners</p>
              </Link>
              <Link to="/blog/coloring-page-prompts" className="p-4 bg-white rounded-xl border border-slate-100 hover:border-violet-200 transition-colors">
                <h4 className="font-semibold text-slate-800 mb-1">50 Free Coloring Page Prompts</h4>
                <p className="text-slate-500 text-sm">Ideas to spark your creativity</p>
              </Link>
            </div>
          </section>
        </div>
      </article>

      {/* Footer */}
      <footer className="border-t border-slate-100 py-8 px-6">
        <div className="max-w-6xl mx-auto text-center text-slate-500 text-sm">
          <p>© 2026 ColorWonder. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default ArticlePrintableColoringPagesGenerator;
